import { useEffect, useState, type CSSProperties } from "react";
import { adminGrantRole, adminListStaff } from "../../lib/api";
import type { AdminStaffMember } from "../../types/admin";
import type { BarRole } from "../../types/auth";
import { useIsMobile } from "../../lib/useIsMobile";
import OperationsStudioTabs from "../../components/admin/OperationsStudioTabs";

const ALL_BAR_ROLES: BarRole[] = [
  "bar_staff",
  "bar_host",
  "bar_manager",
  "bar_owner",
  "bar_bartender",
  "bar_lead",
];

const ROLES: { value: BarRole; label: string; description: string; summary: string }[] = [
  { value: "bar_staff",   label: "Staff",   description: "Floor tools (Cash, Shelf Map, Checklists, Pick List) & Station Mode", summary: "Floor Operations & Cash Drawer" },
  { value: "bar_host",    label: "Host",    description: "Inherits Staff + Host Stand check-in & table intake", summary: "Host Stand & Floor Operations" },
  { value: "bar_manager", label: "Manager", description: "Inherits Staff & Host + Back Office, Inventory & Reports", summary: "Back Office, Host Stand & Floor Ops" },
  { value: "bar_owner",   label: "Owner",   description: "Full access: Specs, Syrups, Staff Roles & Security", summary: "Full Speakeasy & Admin Access" },
];

const DEACTIVATED_NOTE =
  "Deactivated in Square Team — roles can only be removed until they are reactivated there.";

function getMemberLevel(m: AdminStaffMember): BarRole | null {
  if (m.bar_roles.includes("bar_owner")) return "bar_owner";
  if (m.bar_roles.includes("bar_manager")) return "bar_manager";
  if (m.bar_roles.includes("bar_host")) return "bar_host";
  if (m.bar_roles.some((r) => r === "bar_staff" || r === "bar_bartender" || r === "bar_lead")) return "bar_staff";
  return null;
}

export default function AdminStaffAccess() {
  const isMobile = useIsMobile(768);
  const [staff, setStaff] = useState<AdminStaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  function load() {
    setLoading(true);
    setError(null);
    void adminListStaff()
      .then((r) => setStaff(r.staff))
      .catch((err) => setError(err?.message ?? "Could not load staff"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function selectLevel(member: AdminStaffMember, targetRole: BarRole) {
    const key = `${member.id}:${targetRole}`;
    setBusyKey(key);
    setFeedback(null);
    setError(null);
    const currentLevel = getMemberLevel(member);
    const isDeselecting = currentLevel === targetRole;

    // A deactivated employee (is_staff=false) can only have roles removed;
    // bar-admin-grant-role refuses grants on such rows with 409.
    if (!member.is_staff && !isDeselecting) {
      setError(
        `${member.first_name ?? member.email} is deactivated in Square Team — Station roles can only be removed until they are reactivated there.`
      );
      setBusyKey(null);
      return;
    }

    try {
      if (isDeselecting) {
        // Clear all roles
        await Promise.allSettled(
          ALL_BAR_ROLES.map((r) => adminGrantRole(member.id, r, false))
        );
        setStaff((prev) =>
          member.is_staff
            ? prev.map((m) =>
                m.id === member.id
                  ? {
                      ...m,
                      staff_roles: [],
                      bar_roles: [],
                      has_station_access: false,
                    }
                  : m
              )
            : // A deactivated member is only listed because of the role just
              // removed; drop the row now rather than leave four dead buttons.
              prev.filter((m) => m.id !== member.id)
        );
        setFeedback(`Removed role access for ${member.first_name ?? member.email}`);
      } else {
        // 1. Grant new target role
        await adminGrantRole(member.id, targetRole, true);

        // 2. Revoke all other roles in parallel to enforce strict single-level exclusivity
        const rolesToRevoke = ALL_BAR_ROLES.filter((r) => r !== targetRole);
        await Promise.allSettled(
          rolesToRevoke.map((r) => adminGrantRole(member.id, r, false))
        );

        const newRoles = [targetRole];
        setStaff((prev) =>
          prev.map((m) =>
            m.id === member.id
              ? {
                  ...m,
                  staff_roles: newRoles,
                  bar_roles: newRoles,
                  has_station_access: true,
                }
              : m
          )
        );
        const roleLabel = ROLES.find((r) => r.value === targetRole)?.label ?? targetRole;
        setFeedback(
          `Assigned ${roleLabel} access to ${member.first_name ?? member.email}`
        );
      }
    } catch (err: any) {
      setError(err?.message ?? "Could not update role");
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <section style={styles.shell}>
      <OperationsStudioTabs activeTab="staff" badgeCounts={{ staff: staff.length }} />
      <header>
        <p style={styles.eyebrow}>Owner only</p>
        <h1 style={styles.title}>Staff Access</h1>
        <p style={styles.body}>
          Select <strong>one access level</strong> per staff member. Choosing a tier automatically grants that level and inherits all lower permissions:
          <strong> Staff</strong> &rarr; <strong>Host</strong> &rarr; <strong>Manager</strong> &rarr; <strong>Owner</strong>.
        </p>
      </header>

      {feedback && <div style={styles.feedback}>{feedback}</div>}
      {error && <div style={styles.error}>{error}</div>}

      {loading ? (
        <p style={styles.muted}>Loading staff…</p>
      ) : isMobile ? (
        <div style={styles.cardList}>
          {staff.map((m) => {
            const display = `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim() || m.email || "(unnamed)";
            const currentLevel = getMemberLevel(m);
            const activeRoleObj = ROLES.find((r) => r.value === currentLevel);
            const deactivated = !m.is_staff;

            return (
              <div key={m.id} style={styles.card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={styles.nameLine}>{display}</div>
                    <div style={styles.subLine}>{m.email}</div>
                    {deactivated && <div style={styles.deactivatedNote}>{DEACTIVATED_NOTE}</div>}
                  </div>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: currentLevel ? "var(--gold)" : "var(--muted)",
                    background: currentLevel ? "rgba(249, 175, 54, 0.12)" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${currentLevel ? "rgba(249, 175, 54, 0.3)" : "var(--border)"}`,
                    padding: "2px 8px",
                    borderRadius: 8,
                    textTransform: "uppercase",
                  }}>
                    {activeRoleObj ? activeRoleObj.label : "No Access"}
                  </span>
                </div>

                <div style={styles.roleChips}>
                  {ROLES.map((r) => {
                    const active = currentLevel === r.value;
                    const key = `${m.id}:${r.value}`;
                    const busy = busyKey === key;
                    const blocked = deactivated && !active;
                    return (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => void selectLevel(m, r.value)}
                        disabled={busy || blocked}
                        style={{
                          ...styles.roleChip,
                          background: active ? "var(--gold)" : "var(--surface)",
                          color: active ? "#0D0B08" : "var(--muted)",
                          borderColor: active ? "var(--gold)" : "var(--border)",
                          opacity: busy || blocked ? 0.5 : 1,
                          fontWeight: active ? 800 : 600,
                        }}
                        aria-pressed={active}
                        title={blocked ? DEACTIVATED_NOTE : active && deactivated ? "Remove this role" : r.description}
                      >
                        {busy ? "…" : active ? `✓ ${r.label}` : r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Staff member</th>
                <th style={{ ...styles.th, width: 380, textAlign: "center" }}>Access Level (Select One)</th>
                <th style={styles.th}>Permissions Granted</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((m) => {
                const display = `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim() || m.email || "(unnamed)";
                const currentLevel = getMemberLevel(m);
                const activeRoleObj = ROLES.find((r) => r.value === currentLevel);
                const deactivated = !m.is_staff;

                return (
                  <tr key={m.id}>
                    <td style={styles.tdName}>
                      <div style={styles.nameLine}>{display}</div>
                      <div style={styles.subLine}>{m.email}</div>
                      {deactivated && <div style={styles.deactivatedNote}>{DEACTIVATED_NOTE}</div>}
                    </td>
                    <td style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)" }}>
                      <div style={styles.desktopSegmentedBar}>
                        {ROLES.map((r) => {
                          const active = currentLevel === r.value;
                          const key = `${m.id}:${r.value}`;
                          const busy = busyKey === key;
                          const blocked = deactivated && !active;
                          return (
                            <button
                              key={r.value}
                              type="button"
                              onClick={() => void selectLevel(m, r.value)}
                              disabled={busy || blocked}
                              style={{
                                ...styles.segmentedBtn,
                                background: active ? "var(--gold)" : "transparent",
                                color: active ? "#0D0B08" : "var(--muted)",
                                fontWeight: active ? 800 : 600,
                                opacity: busy || blocked ? 0.5 : 1,
                              }}
                              aria-pressed={active}
                              title={blocked ? DEACTIVATED_NOTE : active && deactivated ? "Remove this role" : r.description}
                            >
                              {busy ? "…" : active ? `✓ ${r.label}` : r.label}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td style={{ padding: "12px", borderBottom: "1px solid var(--border)", fontSize: 12, color: "var(--muted)" }}>
                      {activeRoleObj ? (
                        <span style={{ color: "var(--text)" }}>{activeRoleObj.summary}</span>
                      ) : (
                        <span style={{ fontStyle: "italic" }}>No Station Access</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const styles: Record<string, CSSProperties> = {
  shell: { maxWidth: 980 },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    margin: "8px 0 8px",
    letterSpacing: -0.4,
  },
  body: { color: "var(--muted)", fontSize: 13, lineHeight: 1.5, margin: "0 0 16px" },
  deactivatedNote: { marginTop: 4, fontSize: 11, fontWeight: 700, color: "#FF8A7A" },
  feedback: {
    background: "rgba(39,174,96,0.16)",
    border: "1px solid rgba(39,174,96,0.4)",
    color: "#86efac",
    padding: 10,
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 12,
  },
  error: {
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    padding: 10,
    borderRadius: 10,
    fontSize: 13,
    marginBottom: 12,
  },
  tableWrap: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    overflow: "auto",
  },
  table: { borderCollapse: "collapse", width: "100%" },
  th: {
    textAlign: "left",
    padding: 12,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--muted)",
    borderBottom: "1px solid var(--border)",
  },
  thRole: {
    textAlign: "center",
    padding: 12,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--muted)",
    borderBottom: "1px solid var(--border)",
    minWidth: 100,
  },
  roleHint: {
    fontSize: 9,
    fontWeight: 600,
    color: "var(--muted)",
    marginTop: 4,
    textTransform: "none",
    letterSpacing: 0,
    lineHeight: 1.2,
  },
  tdName: {
    padding: 12,
    fontSize: 13,
    fontWeight: 700,
    borderBottom: "1px solid var(--border)",
  },
  nameLine: { color: "var(--text)" },
  subLine: {
    color: "var(--muted)",
    fontSize: 11,
    fontWeight: 600,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    marginTop: 2,
  },
  tdRole: {
    padding: 6,
    textAlign: "center",
    borderBottom: "1px solid var(--border)",
  },
  toggle: {
    width: 40,
    height: 40,
    borderRadius: 999,
    border: "1px solid",
    fontSize: 16,
    fontWeight: 900,
    cursor: "pointer",
  },
  muted: { color: "var(--muted)", fontSize: 13 },
  cardList: { display: "flex", flexDirection: "column", gap: 10 },
  card: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 12,
  },
  desktopSegmentedBar: {
    display: "inline-flex",
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    padding: 3,
    gap: 3,
    width: "100%",
    maxWidth: 360,
  },
  segmentedBtn: {
    flex: 1,
    height: 32,
    border: "none",
    borderRadius: 6,
    fontSize: 12,
    cursor: "pointer",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    transition: "all 0.15s ease",
  },
  roleChips: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 6,
    marginTop: 10,
    width: "100%",
  },
  roleChip: {
    height: 38,
    padding: "0 2px",
    borderRadius: 8,
    border: "1px solid",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    width: "100%",
    boxSizing: "border-box",
  },
};
